import { resources } from "@captions-web/i18n/locales/studio";
import {
  getForcedLanguageLocale,
  getLocaleLanguage,
} from "@captions-web/i18n/utils/getLanguageLocale";
import i18next, { type i18n as I18n } from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { useState, useEffect } from "react";
import { initReactI18next, I18nextProvider } from "react-i18next";
import useLocalStorage from "use-local-storage-state";

const LOCALSTORAGE_LOCALE_KEY = "studio_user_locale";

export const config = {
  defaultLanguage: "en",
  defaultLocale: "en-US",
  supportedLngs: ["en", "pt", "de", "hi"],
  defaultNS: "common",
} as const;

function createI18n({ initialLanguage }: { initialLanguage: string }) {
  i18next.use(initReactI18next);
  i18next.init({
    debug: false,
    lng: initialLanguage,
    fallbackLng: config.defaultLanguage,
    defaultNS: config.defaultNS,
    resources,
    initImmediate: false,
    supportedLngs: config.supportedLngs,
    // `en-US` will be valid if `en` is in supportedLngs.
    nonExplicitSupportedLngs: true,
    interpolation: {
      // React already escapes by default
      escapeValue: false,
    },
  });

  i18next.services.formatter?.add("stripspaces", (value) => value.toString().replace(/\s+/g, ""));

  return i18next;
}

export function StudioI18nProvider({ children }: React.PropsWithChildren) {
  const [localeKey, setLocaleKey] = useLocalStorage<string>(LOCALSTORAGE_LOCALE_KEY, {
    defaultValue: getDefaultLanguage(),
  });
  const [i18n] = useState<I18n>(() => createI18n({ initialLanguage: localeKey }));

  useEffect(() => {
    const languageChanged = (language: string) => {
      setLocaleKey(language);
    };
    i18n?.on("languageChanged", languageChanged);

    return () => i18n?.off("languageChanged", languageChanged);
  }, [i18n, setLocaleKey]);

  return (
    <I18nextProvider i18n={i18n} defaultNS={config.defaultNS} key={localeKey}>
      {children}
    </I18nextProvider>
  );
}

export function getDefaultLanguage() {
  const languageDetector = new LanguageDetector(null, {
    caches: ["localStorage"],
    lookupLocalStorage: LOCALSTORAGE_LOCALE_KEY,
  });

  const browserLanguages: string[] | undefined = languageDetector.detectors?.navigator?.lookup?.();

  const browserLanguage = browserLanguages?.find((browserLanguage: string) => {
    const isSupportedLanguage = config.supportedLngs.some(
      (supportedLanguage) => getLocaleLanguage(browserLanguage) === supportedLanguage
    );

    return isSupportedLanguage;
  });

  return getForcedLanguageLocale(browserLanguage) || config.defaultLocale;
}
